import { gql, useQuery } from "@apollo/client"
import type {
  AllDocumentsDataQuery,
  ApplicationsPartnerQuery,
  OpptiesByPartnerQuery
} from "./useGetApplications.generated-typings"
import { useTokenData } from "./useTokenData"

export type ApplicationWithTask = ApplicationsPartnerQuery["applications_partner"] & {
  documents?: boolean
}

export const GET_APPLICATIONS = gql`
  query applicationsPartner {
    applications_partner {
      payload
      created_at
      updated_at
      application_id
      application_status
      email
      vat_code
      url_from
      url_to
    }
  }
`

export const ALL_DOCUMENTS_TASKS_QUERY = gql`
  query allDocumentsData {
    documentation_overview(
      where: {
        target: { _eq: "PARTNER" }
        task_status: { _eq: "WAITING_DOCUMENTS" }
        requested_document_type: { _neq: "MANDATO_MEDIAZIONE" }
      }
      order_by: { verification_timestamp: desc }
    ) {
      application_id
      document_allowed_mime_types
      task_status
      task_id
      opportunity_id
      requested_document_type
      verified_document_type
      verification_outcome
      verification_reason
      document_description
    }
  }
`

export const OPPTY_BY_PARTNER = gql`
  query opptiesByPartner($vat: String!) {
    credimi_futuro_dossiers(where: { partner_company: { vat_code: { _eq: $vat } } }) {
      application_task
      loans {
        amount
      }
      partner_company {
        vat_code
      }
    }
  }
`

export const useGetApplications = () => {
  const { data: tokenData, isLoading: isLoadingTokenData, error: errorTokenData } = useTokenData()

  const { data, loading, error } = useQuery<ApplicationsPartnerQuery>(GET_APPLICATIONS, {
    fetchPolicy: "network-only"
  })

  const {
    data: documentsData,
    loading: loadingDocumentsData,
    error: errorDocumentsData
  } = useQuery<AllDocumentsDataQuery>(ALL_DOCUMENTS_TASKS_QUERY, {
    fetchPolicy: "network-only"
  })

  const {
    data: opptiesData,
    loading: loadingOpptiesData,
    error: errorOpptiesData
  } = useQuery<OpptiesByPartnerQuery>(OPPTY_BY_PARTNER, {
    variables: { vat: tokenData?.partnerVatCode },
    skip: !tokenData?.partnerVatCode,
    fetchPolicy: "network-only"
  })

  const applicationsPartner: ApplicationsPartnerQuery["applications_partner"] & { documentsTask: boolean }[] = []

  if (data?.applications_partner && documentsData?.documentation_overview && opptiesData?.credimi_futuro_dossiers) {
    data.applications_partner.forEach(application => {
      const docs = documentsData.documentation_overview.filter(
        doc => doc.application_id === application?.application_id && !doc.task_id?.endsWith("_application")
      )
      const agreedAmount = opptiesData?.credimi_futuro_dossiers.filter(
        oppty => oppty.application_task === application?.application_id
      )[0]?.loans?.amount

      applicationsPartner.push({
        ...application,
        payload: {
          ...application?.payload,
          documentsTask: docs.length > 0,
          agreedAmount: agreedAmount ?? null
        }
      })
    })
  }

  return {
    loading: loading || loadingDocumentsData || isLoadingTokenData || loadingOpptiesData,
    error: error || errorDocumentsData || errorOpptiesData || errorTokenData,
    data: applicationsPartner
  }
}
